import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { LegalDoc } from '../components/Legal.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  legalKicker: 'Legal',
  legalTitle: 'Terms of service.',
  legalLede: 'The rules of the road for using SaySomethingIn. Written so you can actually read them – none of this is meant to catch you out.',
  legalLastUpdated: 'Last updated: 14 April 2026',
  legalReadTime: '~5 min read',
  legalIntro: "By using SaySomethingIn you're agreeing to the terms below. We've done our best to write them in plain English; if anything is unclear, email admin@saysomethingin.com and we'll happily explain.",
  legalContactNote: "Anything on this page you'd like to push back on, or something we've missed? Write to admin@saysomethingin.com and we'll reply personally.",

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Document head', keys: ['legalKicker','legalTitle','legalLede','legalLastUpdated','legalReadTime','legalIntro','legalContactNote'], long:['legalLede','legalIntro','legalContactNote'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

const SECTIONS = [
  {
    id: 'who-we-are',
    heading: 'Who we are',
    body: [
      "SaySomethingIn is a trading name of SaySomethingIn Cyf., a private limited company registered in Wales (company number 07234101), with its registered office at Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE. When these terms say 'we', 'us' or 'our', that's who we mean.",
      "'You' means anyone with an SSi account, a free trial, or anyone using the public site.",
    ],
  },
  {
    id: 'your-account',
    heading: 'Your account',
    body: [
      "You need to be 16 or over to open an account. If you're younger than that, ask a parent or guardian to sign up on your behalf.",
      "You're responsible for keeping your password safe and for anything that happens on your account. If you think someone else has got in, let us know at admin@saysomethingin.com and we'll reset it.",
      "One account per person, please. Sharing an account between multiple people confuses progress tracking and isn't fair on us – if you need multiple accounts for a household or team, get in touch and we'll sort out a family or group plan.",
    ],
  },
  {
    id: 'subscriptions-and-payment',
    heading: 'Subscriptions and payment',
    body: [
      'Subscriptions renew automatically on the same date each month or year until you cancel. You can cancel any time from your account settings – monthly subscribers stop billing at the end of the current month; annual subscribers keep access until the end of the year they paid for.',
      'We never raise the price on existing subscribers. The price you sign up at is the price you keep, for as long as your subscription runs uninterrupted. If you cancel and come back later, the then-current price applies.',
      "Payments are processed by Stripe. We don't store card numbers on our servers. Prices include UK VAT where applicable.",
    ],
  },
  {
    id: 'the-seven-day-trial',
    heading: 'The 7-day free trial',
    body: [
      "New subscribers get full access for 7 days before the first charge. You can cancel any time during those 7 days and you won't be charged a penny. If you don't cancel, we'll take the first payment on day 8 and you'll carry on.",
      "The trial is once per person. If you've trialled before, we won't be able to offer another one – but the cancel-any-time refund covers you instead (see below).",
    ],
  },
  {
    id: 'refunds',
    heading: 'Refunds',
    body: [
      "If you've put the work in and it genuinely hasn't clicked for you, we'll refund you – no quiz, no gauntlet of retention emails. Email admin@saysomethingin.com within your first month and we'll sort it.",
      'For the full detail, see our refunds page.',
    ],
  },
  {
    id: 'what-you-can-and-cant-do',
    heading: "What you can and can't do with the content",
    body: [
      "Everything on the site and in the app – the lessons, the audio, the illustrations, the code – belongs to SaySomethingIn Cyf. or to the people we've licensed it from. You can use it to learn a language. You can't redistribute it, resell it, or use it to train machine-learning models without written permission.",
      "You own what you create – your voice recordings, your notes, anything you type into the app. We don't claim any ownership of those.",
    ],
  },
  {
    id: 'acceptable-use',
    heading: 'Acceptable use',
    body: [
      "Don't try to break into the app, scrape it at industrial scale, or use it to spam our community members. These are the things that cost us time to clean up and push prices up for everyone else. We'll close accounts that do them.",
    ],
  },
  {
    id: 'changes-to-the-service',
    heading: 'Changes to the service',
    body: [
      "We're always improving the app and occasionally we'll retire features that aren't paying their way. If we remove something you were actively using we'll email you first.",
      "We reserve the right to suspend the service for maintenance. We try to do this overnight UK time; anything longer than a couple of hours, we'll flag in advance.",
    ],
  },
  {
    id: 'liability',
    heading: 'Liability',
    body: [
      "Nothing in these terms limits our liability for things you can't legally limit liability for – death or personal injury caused by negligence, fraud, that kind of thing.",
      "For everything else, our total liability for any claim is capped at the amount you've paid us in the 12 months before the claim. We're a small company; we can't insure against unlimited risk and still charge what we charge.",
    ],
  },
  {
    id: 'governing-law',
    heading: 'Governing law',
    body: [
      "These terms are governed by the laws of England and Wales, and the courts of England and Wales have exclusive jurisdiction over any dispute. If you're a consumer outside the UK, you also keep the rights you have under the law of the country you live in.",
    ],
  },
];

export default function Terms() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Terms of service">
      <Header t={tweaks}/>
      <main>
        <LegalDoc t={tweaks} sections={SECTIONS}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
